/* ============================================================
   Dathent — AutopilotWidget
   Visual matches design/pages/autopilot.jsx (progress track with
   phase tick marks, per-step progress bar, animated orb), but state
   comes from /api/autopilot/state (real backend), not a demo clock.

   How the clock works in production:
   - The "where are we in the week" position is derived from the
     real local day-of-week + time, so the track fill at any moment
     reflects the actual position in the Sun→Sat cycle.
   - The active step shows an ambient progress animation driven by
     a 14s requestAnimationFrame loop — that animation is purely
     decorative ("the system is alive"), it does NOT reflect any
     real backend progress (one phase run is ~3 min, not animatable).
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { api } from '../../services/api/client.js';

/* Phase manifest. Matches dathent-autopilot-worker:
     PHASE_BY_DOW[0] = 'plan'    (Sunday)
     PHASE_BY_DOW[5] = 'review'  (Friday)
     PHASE_BY_DOW[6] = 'tune'    (Saturday)
   `phase` is the normalized position [0..1) on the weekly track — used
   to place the tick marks and to derive which task is "now" when there
   is no live phase running on the backend. */
const AP_TASKS = [
  { id: 'plan',    label: 'Plan next week',       detail: 'Generate 7 posts',           icon: 'sparkles', day: 'Sun', dur: '~3 min', phase: 0.05 },
  { id: 'compose', label: 'Compose visuals',      detail: 'Image gen + carousels',      icon: 'image',    day: 'Mon', dur: '~2 min', phase: 0.18 },
  { id: 'publish', label: 'Publish & cross-post', detail: 'Daily slots across channels', icon: 'send',     day: '·',   dur: 'continuous', phase: 0.40 },
  { id: 'review',  label: 'Review performance',   detail: 'Analyze last week',          icon: 'trend',    day: 'Fri', dur: '~1 min', phase: 0.66 },
  { id: 'tune',    label: 'Tune content plan',    detail: 'Rebalance pillars',          icon: 'target',   day: 'Sat', dur: '~30 s', phase: 0.84 },
  { id: 'rest',    label: 'Rest',                 detail: 'Until Sunday',               icon: 'clock',    day: '·',   dur: 'idle',  phase: 0.96 },
];

/* Position in the week 0..1 — Sun 00:00 = 0, Sat 23:59 = ~1. */
function weekProgress() {
  const now = new Date();
  const dow = now.getDay(); // 0=Sun
  const ms = (dow * 24 * 3600 * 1000)
           + (now.getHours() * 3600 * 1000)
           + (now.getMinutes() * 60 * 1000)
           + (now.getSeconds() * 1000);
  return ms / (7 * 24 * 3600 * 1000);
}

/* Which task lives at position t? Each task owns [phase, next.phase). */
function currentTask(t) {
  for (let i = 0; i < AP_TASKS.length; i += 1) {
    const a = AP_TASKS[i].phase;
    const b = (i + 1 < AP_TASKS.length) ? AP_TASKS[i + 1].phase : 1;
    if (t >= a && t < b) return { idx: i, localProgress: (t - a) / (b - a) };
  }
  return { idx: AP_TASKS.length - 1, localProgress: 1 };
}

/* Pick spotlight task index from server state. Server currentPhase wins;
   otherwise derive from weekProgress (where in the cycle are we now). */
function activeTaskIndex(state, weekT) {
  const phase = state?.currentPhase;
  if (phase) {
    // When a phase is actively running, spotlight the matching task.
    if (phase === 'plan') return 0;
    if (phase === 'review') return 3;
    if (phase === 'tune') return 4;
    if (phase === 'rest') return 5;
  }
  return currentTask(weekT).idx;
}

function formatCountdown(nextRunAt) {
  if (!nextRunAt) return null;
  const ms = new Date(nextRunAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 'starting soon';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} h`;
  const days = Math.round(hours / 24);
  return `in ${days} d`;
}

/* Ambient animation: a 0→1 cycle every 14s that paints the per-step
   progress bar inside the "now" task. We only run rAF while the widget
   is enabled and is in a "running" state to avoid burning CPU on idle
   dashboards. */
function useAmbientClock(active) {
  const [t, setT] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!active) {
      setT(0);
      return undefined;
    }
    const start = performance.now();
    const tick = (now) => {
      const elapsed = (now - start) / 14000;
      setT(elapsed % 1);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active]);
  return t;
}

export default function AutopilotWidget() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // weekT recomputes on every soft-refresh so the track fill drifts
  // through the week naturally without needing a per-second rAF.
  const [weekT, setWeekT] = useState(() => weekProgress());

  useEffect(() => {
    let cancelled = false;
    const fetchState = async () => {
      try {
        const res = await api.autopilot.getState();
        if (cancelled) return;
        const payload = res?.data?.state ?? res?.state ?? null;
        setState(payload);
        setWeekT(weekProgress());
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load autopilot');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchState();
    // 60s refresh — backend phases are cron-driven and slow (~15 min
    // cadence), so polling more often than this would be wasted work.
    const interval = setInterval(fetchState, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const enabled = !!state?.enabled;
  const phaseRunning = !!state?.currentPhase && state.currentPhase !== 'rest';
  const activeIdx = useMemo(() => activeTaskIndex(state, weekT), [state, weekT]);
  const task = AP_TASKS[activeIdx];

  // Ambient clock animates only when enabled — paused otherwise.
  const ambientT = useAmbientClock(enabled);

  const countdown = formatCountdown(state?.nextRunAt);

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const tz = state?.timezone
        || Intl.DateTimeFormat().resolvedOptions().timeZone
        || 'UTC';
      const res = await api.autopilot.setState({ enabled: !enabled, timezone: tz });
      const payload = res?.data?.state ?? res?.state ?? null;
      setState(payload);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to save autopilot');
    } finally {
      setSaving(false);
    }
  };

  // Skeleton — keep card shape stable on first paint.
  if (loading && !state) {
    return (
      <section className="ap-card">
        <div className="ap-bg" />
        <header className="ap-h">
          <div className="ap-h-l">
            <span className="ap-h-mark"><I.sparkles size={14} /></span>
            <div>
              <div className="eyebrow"><span>Autopilot</span></div>
              <h3 className="ap-title">Loading…</h3>
            </div>
          </div>
        </header>
      </section>
    );
  }

  // Track fill represents WHERE WE ARE in the week — sits still between
  // 60s polls (week ticks slowly), but moves visibly across a day.
  const trackFillPct = (enabled ? (weekT * 100) : 0);

  return (
    <section className="ap-card">
      <div className="ap-bg" />
      <div className="ap-orb" data-active={enabled ? 'true' : 'false'} />

      <header className="ap-h">
        <div className="ap-h-l">
          <span className="ap-h-mark" data-active={enabled ? 'true' : 'false'}>
            <I.sparkles size={14} />
          </span>
          <div>
            <div className="eyebrow"><span>Autopilot</span></div>
            <h3 className="ap-title">
              {enabled ? (
                phaseRunning ? (
                  <>Running — <span className="ap-title-ac">{task.label.toLowerCase()}</span></>
                ) : (
                  <>Standing by — <span className="ap-title-ac">{task.label.toLowerCase()}</span></>
                )
              ) : (
                <>Paused — manual mode</>
              )}
            </h3>
          </div>
        </div>

        <button
          className={`ap-toggle ${enabled ? 'is-on' : ''}`}
          onClick={toggle}
          disabled={saving}
          aria-label="Toggle autopilot"
          type="button"
        >
          <span className="ap-toggle-knob" />
          <span className="ap-toggle-l">{saving ? '…' : (enabled ? 'ON' : 'OFF')}</span>
        </button>
      </header>

      {/* Progress track with phase tick marks. Fill width = position in the
          week; ticks are placed at each task's `phase` value. */}
      <div className="ap-track">
        <div className="ap-track-bg" />
        {AP_TASKS.map((s, i) => (
          <div
            key={s.id}
            className={`ap-tick ${i === activeIdx ? 'is-active' : ''} ${i < activeIdx ? 'is-done' : ''}`}
            style={{ left: `${s.phase * 100}%` }}
            title={s.label}
          >
            <span className="ap-tick-dot" />
          </div>
        ))}
        <div className="ap-fill" style={{ width: `${trackFillPct}%` }}>
          <span className="ap-fill-head" />
        </div>
      </div>

      {/* Step strip — six task cards. The "now" one shows an ambient
          progress bar to communicate "system is alive". */}
      <div className="ap-strip">
        {AP_TASKS.map((s, i) => {
          const TaskIcon = I[s.icon] || I.sparkles;
          const stepState = i < activeIdx ? 'done' : i === activeIdx ? 'now' : 'soon';
          return (
            <div key={s.id} className={`ap-step ap-step-${stepState}`}>
              <span className="ap-step-ic">
                {stepState === 'done' ? <I.check size={11} /> : <TaskIcon size={11} />}
              </span>
              <div className="ap-step-text">
                <div className="ap-step-l">{s.label}</div>
                <div className="ap-step-s">
                  <span className="mono">{s.day}</span>
                  <span className="ap-d-sep">·</span>
                  <span>{s.dur}</span>
                </div>
              </div>
              {stepState === 'now' && enabled && (
                <div className="ap-step-progress">
                  <div className="ap-step-progress-fill" style={{ width: `${ambientT * 100}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer — countdown / last-run hint on the left, deep link
          to /app/plan on the right (NOT /app/content-plan — that
          legacy URL falls into the old MainApp catch-all). */}
      <footer className="ap-foot">
        <span className="ap-foot-l">
          {error ? (
            <span style={{ color: 'var(--danger)' }}>{error}</span>
          ) : enabled ? (
            <>
              {state?.lastRunSummary ? <>Last run: <b>{state.lastRunSummary}</b></> : <>Next: <b>{AP_TASKS[(activeIdx + 1) % AP_TASKS.length].label}</b></>}
              {countdown && <> · {countdown}</>}
            </>
          ) : (
            <>Turn on autopilot to let Dathent run your weekly cycle.</>
          )}
        </span>
        <Link to="/app/plan" className="ap-foot-link">
          Customize cycle <I.arrowRight size={11} />
        </Link>
      </footer>
    </section>
  );
}
